import { ReactElement } from 'react'

import type { BlockCreateInvoiceActionReason } from '../../enums/blockCreateInvoiceActionReason'
import type { Quote } from '../../types/quotes/quote'
import { getBlockCreateDepositInvoiceActionReason } from '../../utils/depositInvoices/getBlockCreateDepositInvoiceActionReason'
import { getBlockCreateInvoiceActionReason } from '../../utils/depositInvoices/getBlockCreateInvoiceActionReason'
import { getQuoteDepositInvoice } from '../../utils/depositInvoices/getQuoteDepositInvoice'
import { getQuoteDepositInvoiceDraft } from '../../utils/depositInvoices/getQuoteDepositInvoiceDraft'
import { getQuoteFinalInvoiceDraft } from '../../utils/depositInvoices/getQuoteFinalInvoiceDraft'
import { getQuoteRegularInvoiceDraft } from '../../utils/depositInvoices/getQuoteRegularInvoiceDraft'
import { BlockCreateInvoiceActionModal } from '../BlockCreateInvoiceActionModal'
import { getBlockCreateDepositInvoiceActionModalId } from '../BlockCreateInvoiceActionModal/utils/getBlockCreateDepositInvoiceActionModalId'
import { getBlockCreateInvoiceActionModalId } from '../BlockCreateInvoiceActionModal/utils/getBlockCreateInvoiceActionModalId'

interface BlockCreateInvoiceActionModalsProps {
  createInvoiceUrl?: string
  onCreateDepositInvoiceClick?: () => void
  quote?: Quote
  // Needed when several quotes can render this at once (e.g. one row per quote in a table) so each
  // row gets its own modal instance. Leave unset in single-quote views (e.g. QuoteView).
  scopeId?: string
}

interface ApplicableBlockCreateInvoiceAction {
  modalId: string
  reason: BlockCreateInvoiceActionReason
}

export const BlockCreateInvoiceActionModals = ({
  createInvoiceUrl,
  onCreateDepositInvoiceClick,
  quote,
  scopeId,
}: BlockCreateInvoiceActionModalsProps): ReactElement => {
  const existingDepositInvoice = getQuoteDepositInvoice(quote)
  const existingDepositInvoiceDraft = getQuoteDepositInvoiceDraft(quote)
  const existingFinalInvoiceDraft = getQuoteFinalInvoiceDraft(quote)
  const existingRegularInvoiceDraft = getQuoteRegularInvoiceDraft(quote)

  // Each bucket has its own reason function (one for create-invoice/-final-invoice, one for
  // create-deposit-invoice) - they gate different actions, so at most one applies per bucket,
  // but both buckets are independent and can apply at once.
  const invoiceActionReason = getBlockCreateInvoiceActionReason(quote)
  // A consumer without a "create deposit invoice" action (e.g. ConvertQuoteToFinalInvoiceButton)
  // has no confirm handler for this reason, so don't surface it there.
  const depositInvoiceActionReason = onCreateDepositInvoiceClick
    ? getBlockCreateDepositInvoiceActionReason(quote)
    : undefined

  const applicableActions: ApplicableBlockCreateInvoiceAction[] = []
  if (invoiceActionReason) {
    applicableActions.push({ modalId: getBlockCreateInvoiceActionModalId(scopeId), reason: invoiceActionReason })
  }
  if (depositInvoiceActionReason) {
    applicableActions.push({
      modalId: getBlockCreateDepositInvoiceActionModalId(scopeId),
      reason: depositInvoiceActionReason,
    })
  }

  return (
    <>
      {applicableActions.map(({ modalId, reason }) => (
        <BlockCreateInvoiceActionModal
          blockingReason={reason}
          createInvoiceUrl={createInvoiceUrl}
          depositInvoiceDraftId={existingDepositInvoiceDraft?.invoiceDraftId}
          depositInvoiceId={existingDepositInvoice?.invoiceId}
          existingFinalInvoiceDraft={existingFinalInvoiceDraft}
          id={modalId}
          key={reason}
          onCreateDepositInvoiceClick={onCreateDepositInvoiceClick}
          quoteId={quote?.id}
          regularInvoiceDraftId={existingRegularInvoiceDraft?.invoiceDraftId}
        />
      ))}
    </>
  )
}
